<template>
  <div class="node-list" :style="{ paddingLeft: depth > 2 ? '10px' : '0' }">
    <template v-for="node in nodes" :key="node.id">

      <!-- Sub-group: nested collapse -->
      <template v-if="node.type === 'group'">
        <n-collapse class="sub-collapse" :class="{ 'sub-collapse--hidden': hiddenGroupNodeIds.has(node.id) }"
          :default-expanded-names="[String(node.id)]">
          <n-collapse-item :title="node.title" :name="String(node.id)">
            <template #header-extra>
              <button
                class="eye-btn"
                :title="hiddenGroupNodeIds.has(node.id) ? 'Show on map' : 'Hide on map'"
                @click.stop="toggleGroupVisibility(node.id)"
              >
                <svg v-if="!hiddenGroupNodeIds.has(node.id)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>
            </template>
            <!-- Nested group: childScope() applies the nested group's own
                 exclusive_selection and decides whether it joins our scope -->
            <LayerNodeChildren
              :nodes="node.children"
              :depth="depth + 1"
              :scope="childScope(scope, node)"
            />
          </n-collapse-item>
        </n-collapse>
      </template>

      <!-- Raster type leaf (multiple COGs) — collapsible, expanded by default -->
      <template v-else-if="node.type === 'raster_type'">
        <n-collapse class="sub-collapse" :default-expanded-names="[String(node.id)]">
          <n-collapse-item :title="node.title" :name="String(node.id)">
            <RasterTypeControl :node="node" :scope="scope" />
          </n-collapse-item>
        </n-collapse>
      </template>

      <!-- Static raster layer leaf (single layer — no sub-items to collapse) -->
      <template v-else-if="node.type === 'raster_layer'">
        <div class="leaf-section">
          <RasterLayerControl :node="node" :scope="scope" />
        </div>
      </template>

      <!-- Vector leaf (multiple years) — collapsible, expanded by default -->
      <template v-else-if="node.type === 'vector'">
        <n-collapse class="sub-collapse" :default-expanded-names="[String(node.id)]">
          <n-collapse-item :title="node.title" :name="String(node.id)">
            <VectorLayerControl :node="node" :scope="scope" />
          </n-collapse-item>
        </n-collapse>
      </template>

    </template>
  </div>
</template>

<script setup lang="ts">
import { NCollapse, NCollapseItem } from 'naive-ui'
import type { LayerTreeNode } from '@/types/layerTree'
import { useLayerSelection } from '@/composables/useLayerSelection'
import { childScope, type SelectionScope } from '@/composables/layerScope'
import RasterTypeControl from './RasterTypeControl.vue'
import RasterLayerControl from './RasterLayerControl.vue'
import VectorLayerControl from './VectorLayerControl.vue'

defineProps<{
  nodes: LayerTreeNode[]
  depth: number
  /** Selection scope these nodes belong to (see composables/layerScope.ts). */
  scope: SelectionScope
}>()

const { hiddenGroupNodeIds, toggleGroupVisibility } = useLayerSelection()
</script>

<style scoped>
.node-list { display: flex; flex-direction: column; gap: 2px; }

.sub-collapse {
  border-left: 1px solid #313244;
  margin-left: 4px;
  padding-left: 6px;
}
.sub-collapse--hidden { opacity: 0.45; }

.eye-btn {
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #6c7086;
  display: flex;
  align-items: center;
  border-radius: 3px;
  transition: color 0.12s;
}
.eye-btn:hover { color: #89b4fa; }

.leaf-section {
  padding: 4px 0 4px 4px;
  border-left: 1px solid #313244;
  margin-left: 4px;
}

:deep(.n-collapse-item__header-main) { --n-title-text-color: #cdd6f4 !important; font-size: 12px !important; }
:deep(.n-collapse-item__header) { padding: 6px 0 !important; }
:deep(.n-base-icon) { color: #6c7086 !important; }
</style>
